export const dedupe = <T>(arr: Array<T>) => [...new Set(arr)]

export function intersect<T>(arr: Array<T>, constraintArr: Array<T>) {
  const result = arr.filter((item) => constraintArr.includes(item))
  return result
}

// localeCompare doesn't work correctly for Icelandic (even when explicitly passing the locale)
// Other people have run into this https://twitter.com/sil/status/642093341870587905
// We include C and Q in the alphabet since some company and restaurant names use them.
const iceAlphabet = [
  "?",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "A",
  "Á",
  "B",
  "C",
  "D",
  "Ð",
  "E",
  "É",
  "F",
  "G",
  "H",
  "I",
  "Í",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "Ó",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "Ú",
  "V",
  "W",
  "X",
  "Y",
  "Ý",
  "Þ",
  "Æ",
  "Ö",
  "Z",
]

const compareIcelandicChar = (a: string, b: string): -1 | 0 | 1 => {
  const aInd = iceAlphabet.indexOf(a.toUpperCase())
  const bInd = iceAlphabet.indexOf(b.toUpperCase())

  return aInd === bInd ? 0 : aInd < bInd ? -1 : 1
}

export const compareIcelandic = (a: string, b: string): -1 | 0 | 1 => {
  if (a.length === 0 && b.length === 0) {
    return 0
  }

  if (a.length === 0) {
    return -1
  }

  if (b.length === 0) {
    return 1
  }

  // charAt always returns a string (empty when out of range), so no undefined-index handling is needed
  const n = compareIcelandicChar(a.charAt(0), b.charAt(0))
  if (n !== 0) {
    return n
  }

  return compareIcelandic(a.slice(1), b.slice(1))
}

/**
 * Throws an error if the condition is falsy, with TypeScript type narrowing.
 *
 * After this function call, TypeScript knows the condition is truthy and
 * narrows the type accordingly. This eliminates the need for manual type
 * guards or non-null assertions in subsequent code.
 *
 * @param condition - The value to check; throws if falsy
 * @param message - Optional custom error message
 * @throws {Error} When condition is falsy
 *
 * @example
 * const user: User | null = getUser();
 * assert(user, "User not found");
 * 💡 TypeScript now knows `user` is `User`, not `User | null`
 */
export function assert(condition: unknown, message?: string): asserts condition {
  if (!condition) {
    throw new Error(message ?? "Assertion failed")
  }
}
